// src/components/SimpleComponents/SimpleSelectAsyncEntities.jsx
// *******************************************************************************
// ASYNC SELECT FOR ENTITIES
// *******************************************************************************
// Async select component for searching and selecting entities (customers, providers).
// Uses power-search endpoint with indexed fields for fast autocomplete.
// Searches in: name, fullName, phone.value, email.value (normalized, no accents).
// Displays 2-line format: name + document on left, profile tags + icon on right.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Select, Spin, Flex, Typography, Tag, Empty, Form } from 'antd';
import { SearchOutlined, UserOutlined, ShopOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import axiosService from 'services/axiosService';
import entitiesService from 'services/entitiesService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 10;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelectAsyncEntities = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder = 'search_entity',
  onChange,
  onEntitySelect,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  showProfileTags = true,
  showIcon = true,
  entityProfiles,
  filterFn, // Optional function(entity) => boolean for additional client-side filtering
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  maxResults = MAX_RESULTS,
  selectedEntity = null, // Entity object to display when value is set externally
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const debounceTimerRef = useRef(null);
  const lastSearchRef = useRef('');

  // Watch field value for validation
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Create option from selectedEntity when value is set externally (e.g., after creating new entity)
  useEffect(() => {
    if (!selectedEntity?._id) return;

    // Create option for the selected entity and set it
    // This ensures the Select shows the entity name instead of the _id
    const entityOption = {
      value: selectedEntity._id,
      label: selectedEntity.name || selectedEntity.fullName || selectedEntity.razaoSocial || '-',
      entity: {
        ...selectedEntity,
        __richLabel: buildEntityLabel(selectedEntity, { showProfileTags, showIcon }),
      },
    };

    setOptions(prevOptions => {
      // Check if this entity is already in options
      const existingOption = prevOptions.find(opt => opt.value === selectedEntity._id);
      if (existingOption) return prevOptions;

      // Add the new option (replace all options since this is external selection)
      return [entityOption];
    });
  }, [selectedEntity, showProfileTags, showIcon]);

  // Activate validation when form shows errors
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Validate field value
  useEffect(() => {
    if (!shouldValidate) return;

    const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

    if (!hasValue && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (hasValue) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    async searchValue => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Skip if search is too short
      if (!searchValue || searchValue.length < minSearchLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      // Skip if same search
      const normalizedSearch = searchValue.toLowerCase().trim();
      if (normalizedSearch === lastSearchRef.current) {
        return;
      }

      setLoading(true);

      // Debounce the actual search
      debounceTimerRef.current = setTimeout(async () => {
        try {
          lastSearchRef.current = normalizedSearch;

          // Use power-search endpoint (POST with body) for faster indexed search
          // returnAsObject: false to get the full array of results
          const [entities] = await axiosService.post({
            url: API.ENTITIES.POWER_SEARCH,
            data: { search: searchValue },
            returnAsObject: false,
          });

          // Filter entities on client side
          let filteredEntities = Array.isArray(entities) ? entities : [];

          // Exclude final consumers (generic entities used for NFCe)
          filteredEntities = filteredEntities.filter(entity => !entity.finalConsumer);

          // Filter by entityProfile if specified
          // (power-search doesn't support entityProfile filter yet)
          if (entityProfiles && filteredEntities.length > 0) {
            const profilesArray = Array.isArray(entityProfiles) ? entityProfiles : [entityProfiles];
            filteredEntities = filteredEntities.filter(entity => {
              const entityProfileArray = Array.isArray(entity.entityProfile)
                ? entity.entityProfile
                : [entity.entityProfile];
              return entityProfileArray.some(p => profilesArray.includes(p));
            });
          }

          // Apply custom filter function if provided
          if (filterFn) {
            filteredEntities = filteredEntities.filter(filterFn);
          }

          // Limit results after client-side filtering
          const limitedEntities = filteredEntities.slice(0, maxResults);

          // Transform entities to select options
          // Note: Only use standard Ant Design props (value, label) to avoid DOM warnings
          // Store rich label in entity object for optionRender to access
          const entityOptions = limitedEntities.map(entity => ({
            value: entity._id,
            label: entity.name || entity.fullName || entity.razaoSocial || '-', // Simple text for selected display
            entity: {
              ...entity,
              __richLabel: buildEntityLabel(entity, { showProfileTags, showIcon }), // Rich JSX for dropdown
            },
          }));

          setOptions(entityOptions);
        } catch (error) {
          console.error('[SimpleSelectAsyncEntities] Search error:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceDelay);
    },
    [minSearchLength, maxResults, entityProfiles, filterFn, showProfileTags, showIcon, debounceDelay]
  );

  const handleSelect = useCallback(
    (value, option) => {
      setOpen(false);
      setShouldValidate(true);

      // Call onChange with just the ID
      if (typeof onChange === 'function') {
        onChange(value, option);
      }

      // Call onEntitySelect with full entity data
      if (typeof onEntitySelect === 'function' && option?.entity) {
        onEntitySelect(option.entity);
      }
    },
    [onChange, onEntitySelect]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
    if (!visible) {
      // Clear search state when closing
      lastSearchRef.current = '';
    }
  }, []);

  const handleBlur = useCallback(
    ev => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (typeof onBlur === 'function') {
        setTimeout(() => onBlur(ev), 0);
      }
    },
    [onBlur]
  );

  const handleClear = useCallback(() => {
    setOptions([]);
    lastSearchRef.current = '';
    setShouldValidate(true);

    if (typeof onChange === 'function') {
      onChange(null, null);
    }
    if (typeof onEntitySelect === 'function') {
      onEntitySelect(null);
    }
  }, [onChange, onEntitySelect]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Spin size="small" />
        </Flex>
      );
    }

    if (lastSearchRef.current && options.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('no_entities_found')}
          style={{ margin: '16px 0' }}
        />
      );
    }

    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Text type="secondary">{translateX('type_to_search_entities')}</Text>
      </Flex>
    );
  }, [loading, options.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        style={{ height: 40, ...style }}
        showSearch
        allowClear
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        filterOption={false} // Disable client-side filtering, we use server-side
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        onBlur={handleBlur}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        notFoundContent={notFoundContent}
        loading={loading}
        suffixIcon={loading ? <Spin size="small" /> : <SearchOutlined />}
        options={options}
        optionRender={option => option.data.entity?.__richLabel}
        labelInValue={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSelectAsyncEntities;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build entity label for select option
 * Displays: Name + Document on left | Profile tags + Person/Company icon on right
 * @param {Object} entity - Entity data
 * @param {Object} options - Display options
 * @returns {JSX.Element} Formatted label
 */
function buildEntityLabel(entity, { showProfileTags = true, showIcon = true } = {}) {
  const name = entity.name || entity.fullName || entity.razaoSocial || '-';
  const document = getEntityDocument(entity);

  // Determine if company - check direct field, entityType, or documents array
  let hasCnpj = !!entity.cnpj;
  if (!hasCnpj && entity.documents?.length) {
    hasCnpj = entity.documents.some(d => d.documentType === 'cnpj');
  }
  const isCompany = hasCnpj || entity.entityType === 'company';

  const profiles = entity.entityProfile || [];
  const profileArray = Array.isArray(profiles) ? profiles : [profiles];

  return (
    <Flex justify="space-between" align="center" style={{ width: '100%', padding: '4px 0' }}>
      {/* Left side: Name and document */}
      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
        <Text ellipsis strong style={{ lineHeight: 1.3 }}>
          {name}
        </Text>
        {document && (
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
            {document}
          </Text>
        )}
      </Flex>

      {/* Right side: Profile tags and icon */}
      <Flex align="center" gap={8} style={{ marginLeft: 12 }}>
        {showProfileTags && profileArray.length > 0 && (
          <Flex gap={4} wrap>
            {profileArray.slice(0, 2).map((profile, idx) => (
              <Tag
                key={idx}
                color={entitiesService.getEntityProfileColor(profile)}
                style={{ margin: 0, fontSize: 11 }}
              >
                {translateX(profile)}
              </Tag>
            ))}
            {profileArray.length > 2 && (
              <Tag style={{ margin: 0, fontSize: 11 }}>+{profileArray.length - 2}</Tag>
            )}
          </Flex>
        )}
        {showIcon &&
          (isCompany ? (
            <ShopOutlined style={{ fontSize: 16, color: '#1890ff' }} />
          ) : (
            <UserOutlined style={{ fontSize: 16, color: '#52c41a' }} />
          ))}
      </Flex>
    </Flex>
  );
}

/**
 * Get formatted document string (CPF or CNPJ) from entity
 * Handles both direct fields (cpf, cnpj) and documents array from power-search
 * @param {Object} entity - Entity data
 * @returns {string|null} Formatted document string
 */
function getEntityDocument(entity) {
  // Try direct fields first (pagination response)
  let cpf = entity.cpf?.value || entity.cpf;
  let cnpj = entity.cnpj?.value || entity.cnpj;

  // If not found, try documents array (power-search response)
  if (!cpf && !cnpj && entity.documents?.length) {
    const cpfDoc = entity.documents.find(d => d.documentType === 'cpf');
    const cnpjDoc = entity.documents.find(d => d.documentType === 'cnpj');
    cpf = cpfDoc?.value;
    cnpj = cnpjDoc?.value;
  }

  if (cnpj) return `CNPJ: ${formatCnpj(cnpj)}`;
  if (cpf) return `CPF: ${formatCpf(cpf)}`;

  return null;
}

/**
 * Format CNPJ with mask: XX.XXX.XXX/XXXX-XX
 * @param {string} value - Raw CNPJ value
 * @returns {string} Formatted CNPJ
 */
function formatCnpj(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 14) return value;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Format CPF with mask: XXX.XXX.XXX-XX
 * @param {string} value - Raw CPF value
 * @returns {string} Formatted CPF
 */
function formatCpf(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 11) return value;
  return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}
